import React, { useState, useEffect } from "react";

const CapitalGainsTaxCalculator = () => {
  const [purchasePrice, setPurchasePrice] = useState(500000);
  const [salePrice, setSalePrice] = useState(800000);
  const [assetType, setAssetType] = useState("equity");
  const [holdingPeriod, setHoldingPeriod] = useState(24);
  const [indexationBenefit, setIndexationBenefit] = useState(false);
  const [financialYear, setFinancialYear] = useState("2023-24");
  const [result, setResult] = useState({
    purchasePrice: "0.00",
    salePrice: "0.00",
    capitalGain: "0.00",
    applicableTaxRate: "0.00",
    baseTax: "0.00",
    surcharge: "0.00",
    cess: "0.00",
    totalTax: "0.00",
    netAmount: "0.00",
    gainType: "",
    savingTip: "",
  });

  // Capital Gains Tax Calculation Function
  const calculateCapitalGainsTax = () => {
    let purchase = parseFloat(purchasePrice);
    let sale = parseFloat(salePrice);
    let holding = parseFloat(holdingPeriod);

    if (isNaN(purchase) || purchase < 0 || isNaN(sale) || sale < 0 || isNaN(holding) || holding < 0) return;

    // Calculate capital gain
    let capitalGain = sale - purchase;
    
    // Determine if Short-Term or Long-Term and applicable rate
    let isLongTerm = false;
    let gainType = "";
    let taxRate = 0;
    
    if (assetType === "equity") {
      isLongTerm = holding > 12;
      if (isLongTerm) {
        gainType = "Long-Term Capital Gain (LTCG)";
        taxRate = 10; // 10% without indexation
      } else {
        gainType = "Short-Term Capital Gain (STCG)";
        taxRate = 15; // 15% flat rate
      }
    } else if (assetType === "property" || assetType === "gold") {
      isLongTerm = holding > 24;
      if (isLongTerm) {
        gainType = "Long-Term Capital Gain (LTCG)";
        taxRate = indexationBenefit ? 20 : 10;
      } else {
        gainType = "Short-Term Capital Gain (STCG)";
        taxRate = 30; // As per income tax slab
      }
    } else if (assetType === "debt") {
      isLongTerm = holding > 36;
      if (isLongTerm) {
        gainType = "Long-Term Capital Gain (LTCG)";
        taxRate = indexationBenefit ? 20 : 10;
      } else {
        gainType = "Short-Term Capital Gain (STCG)";
        taxRate = 30;
      }
    }

    // Calculate base tax
    let baseTax = 0;
    if (capitalGain > 0) {
      baseTax = (capitalGain * taxRate) / 100;
    }
    
    // Surcharge calculation (on tax amount for very high gains)
    let surcharge = 0;
    if (capitalGain > 5000000 && capitalGain <= 10000000) {
      surcharge = baseTax * 0.10;
    } else if (capitalGain > 10000000) {
      surcharge = baseTax * 0.15;
    }
    
    // Health and Education Cess (4% on Tax + Surcharge)
    const cess = (baseTax + surcharge) * 0.04;
    
    // Total Tax
    const totalTax = baseTax + surcharge + cess;
    
    // Net amount received after tax
    const netAmount = sale - totalTax;

    let savingTip = "";

    if (assetType === "equity") {
      if (isLongTerm) {
        savingTip = "LTCG on equity above ₹1 lakh taxed at 10%. Hold for >12 months to qualify for LTCG benefits.";
      } else {
        savingTip = "STCG on equity taxed at 15%. Consider holding >12 months for lower LTCG rate.";
      }
    } else if (assetType === "property" || assetType === "gold") {
      if (isLongTerm) {
        savingTip = indexationBenefit 
          ? "LTCG with indexation at 20%. Indexation reduces taxable gains significantly."
          : "LTCG without indexation at 10%. Consider indexation benefit for old assets.";
      } else {
        savingTip = "STCG taxed as per income tax slab. Hold >24 months to qualify for LTCG benefits.";
      }
    } else if (assetType === "debt") {
      if (isLongTerm) {
        savingTip = "LTCG on debt funds. Consider equity investments for better tax efficiency after >12 months.";
      } else {
        savingTip = "STCG on debt added to income. Hold >36 months for LTCG treatment.";
      }
    }

    setResult({
      purchasePrice: purchase.toFixed(2),
      salePrice: sale.toFixed(2),
      capitalGain: capitalGain.toFixed(2),
      applicableTaxRate: taxRate.toFixed(2),
      baseTax: baseTax.toFixed(2),
      surcharge: surcharge.toFixed(2),
      cess: cess.toFixed(2),
      totalTax: totalTax.toFixed(2),
      netAmount: netAmount.toFixed(2),
      gainType,
      savingTip,
    });
  };

  useEffect(() => {
    calculateCapitalGainsTax();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
          </div>
          <h1>Capital Gains Tax Calculator</h1>
          <p>
            Calculate capital gains tax on sale of assets including equity, property, gold, and debt funds under Income Tax Act
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Asset Transaction Details
              </h2>

              {/* Purchase Price */}
              <div className="input-group">
                <label>Purchase Price</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={purchasePrice}
                    onChange={(e) => setPurchasePrice(e.target.value)}
                    className="calc-input"
                    placeholder="500000"
                  />
                </div>
                <div className="quick-select-group">
                  {[100000, 500000, 1000000, 5000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setPurchasePrice(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sale Price */}
              <div className="input-group">
                <label>Sale Price</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={salePrice}
                    onChange={(e) => setSalePrice(e.target.value)}
                    className="calc-input"
                    placeholder="800000"
                  />
                </div>
                <div className="quick-select-group">
                  {[200000, 800000, 2000000, 10000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setSalePrice(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Asset Type */}
              <div className="input-group">
                <label>Asset Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "equity", label: "Equity/Stocks" },
                    { value: "property", label: "Property/Real Estate" },
                    { value: "gold", label: "Gold/Jewellery" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setAssetType(type.value)}
                      className={`quick-select-btn ${assetType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="quick-select-group" style={{marginTop: '0.5rem'}}>
                  {[
                    { value: "debt", label: "Debt Funds/Bonds" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setAssetType(type.value)}
                      className={`quick-select-btn ${assetType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Holding Period */}
              <div className="input-group">
                <label>Holding Period (Months)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">Mo</span>
                  <input
                    type="number"
                    value={holdingPeriod}
                    onChange={(e) => setHoldingPeriod(e.target.value)}
                    className="calc-input"
                    placeholder="24"
                  />
                </div>
                <div className="quick-select-group">
                  {[6, 12, 24, 36].map((months) => (
                    <button
                      key={months}
                      onClick={() => setHoldingPeriod(months)}
                      className="quick-select-btn success"
                    >
                      {months}m
                    </button>
                  ))}
                </div>
              </div>

              {/* Indexation Benefit */}
              <div className="input-group">
                <label>Indexation Benefit (for LTCG)</label>
                <div className="quick-select-group">
                  {[
                    { value: true, label: "With Indexation (20%)" },
                    { value: false, label: "Without Indexation (10%)" }
                  ].map((type) => (
                    <button
                      key={type.value.toString()}
                      onClick={() => setIndexationBenefit(type.value)}
                      className={`quick-select-btn ${indexationBenefit === type.value ? 'success' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Financial Year */}
              <div className="input-group">
                <label>Financial Year</label>
                <div className="quick-select-group">
                  {[
                    { value: "2023-24", label: "FY 2023-24" },
                    { value: "2024-25", label: "FY 2024-25" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setFinancialYear(type.value)}
                      className={`quick-select-btn ${financialYear === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateCapitalGainsTax} className="calculate-btn">
                Calculate Capital Gains Tax
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Capital Gains Calculation
              </h2>

              <div className="result-card">
                {/* Total Tax - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Total Capital Gains Tax</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalTax).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.gainType} at {result.applicableTaxRate}%
                  </div>
                </div>

                {/* Net Amount After Tax */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Net Amount After Tax</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.netAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Amount you receive after tax
                  </div>
                </div>

                {/* Capital Gain */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Capital Gain/Loss</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.capitalGain).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Sale Price - Purchase Price
                  </div>
                </div>

                {/* Sale Price */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Sale Price</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.salePrice).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Asset selling price
                  </div>
                </div>

                {/* Base Tax */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Base Capital Gains Tax</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.baseTax).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    At {result.applicableTaxRate}% tax rate
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Tax Breakdown</h3>
                  
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Base Tax ({result.applicableTaxRate}%)</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.baseTax).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${parseFloat(result.totalTax) > 0 ? (parseFloat(result.baseTax) / parseFloat(result.totalTax)) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>

                  {parseFloat(result.surcharge) > 0 && (
                    <div className="breakdown-item">
                      <div className="breakdown-header">
                        <span className="breakdown-label">Surcharge</span>
                        <span className="breakdown-percentage">
                          ₹{Number(result.surcharge).toLocaleString("en-IN")}
                        </span>
                      </div>
                      <div className="breakdown-bar">
                        <div
                          className="breakdown-bar-fill purple"
                          style={{
                            width: `${(parseFloat(result.surcharge) / parseFloat(result.totalTax)) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Cess (4%)</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.cess).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${parseFloat(result.totalTax) > 0 ? (parseFloat(result.cess) / parseFloat(result.totalTax)) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Sale Proceeds Distribution */}
                <div className="breakdown-section" style={{marginTop: '1.5rem'}}>
                  <h3 className="breakdown-title">Sale Proceeds Distribution</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Net Amount (After Tax)</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.netAmount) / parseFloat(result.salePrice)) * 100).toFixed(2)}% (₹{Number(result.netAmount).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(parseFloat(result.netAmount) / parseFloat(result.salePrice)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Tax Paid</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.totalTax) / parseFloat(result.salePrice)) * 100).toFixed(2)}% (₹{Number(result.totalTax).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(parseFloat(result.totalTax) / parseFloat(result.salePrice)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Saving Tip Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>Tax Planning Tip</h3>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    {result.savingTip} Consider tax-loss harvesting and exemptions under Section 54/54EC/54F.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Capital Gains Tax in India</h3>
              <p>
                Capital Gains Tax is levied on profits from the sale of capital assets like stocks, property, gold, 
                and mutual funds. Gains are classified as Short-Term Capital Gains (STCG) or Long-Term Capital Gains 
                (LTCG) based on holding period. For equity shares/mutual funds, holding greater than 12 months qualifies as long-term; 
                for property/gold, greater than 24 months; for debt funds, greater than 36 months. STCG on equity is taxed at 15%, while LTCG 
                above ₹1 lakh is taxed at 10% without indexation. For property/gold, LTCG is taxed at 20% with indexation 
                benefit or 10% without. STCG on non-equity assets is added to income and taxed as per applicable slab. 
                Indexation adjusts purchase price for inflation using Cost Inflation Index (CII). Exemptions under Section 54 
                (residential property), 54EC (bonds), and 54F (any asset reinvested in residential property) can reduce 
                tax liability. Capital losses can be set off against gains and carried forward for 8 years.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Capital Gains Tax Calculation Formula:</h4>
            <div className="formula-display">
              Total Tax = Base Tax + Surcharge + Health & Education Cess (4%)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Capital Gain</div>
                <div className="formula-param-value">Sale Price - Purchase Price (or Indexed Cost)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">STCG (Equity)</div>
                <div className="formula-param-value">15% on gains if held ≤12 months</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">LTCG (Equity)</div>
                <div className="formula-param-value">10% on gains >₹1L if held >12 months</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">LTCG (Property/Gold)</div>
                <div className="formula-param-value">20% with indexation or 10% without, if held >24 months</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is the difference between STCG and LTCG?</h5>
              <p>
                Short-Term Capital Gains (STCG) apply when assets are held for shorter periods, while Long-Term Capital Gains (LTCG) apply for longer holdings. Tax rates and benefits differ significantly based on the classification.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is indexation benefit?</h5>
              <p>
                Indexation adjusts the purchase price of an asset for inflation using the Cost Inflation Index (CII), reducing taxable capital gains on long-term assets like property and gold.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I avoid capital gains tax on property sale?</h5>
              <p>
                Yes, under Section 54, invest LTCG in another residential property within specified time. Section 54EC allows investment in specified bonds. Section 54F applies for other assets reinvested in residential property.
              </p>
            </div>

            <div className="faq-item">
              <h5>How is LTCG on equity calculated?</h5>
              <p>
                LTCG on equity above ₹1 lakh per year is taxed at 10% without indexation benefit. Gains up to ₹1 lakh annually are tax-free for equity shares held over 12 months.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I set off capital losses against gains?</h5>
              <p>
                Yes, STCG losses can be set off against both STCG and LTCG. LTCG losses can only be set off against LTCG. Unabsorbed losses can be carried forward for 8 years.
              </p>
            </div>

            <div className="faq-item">
              <h5>When should I pay capital gains tax?</h5>
              <p>
                Capital gains tax is paid when filing your Income Tax Return (ITR). However, advance tax must be paid during the year if tax liability exceeds ₹10,000.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapitalGainsTaxCalculator;